import React from 'react';
import {
    Tag,
    Avatar,
    makeStyles,
    mergeClasses
} from '@fluentui/react-components';

// Interfaces from your 360-react-views
interface ElementWithKey {
    key?: React.Key | null;
}

interface ElementWithKeyAndText extends ElementWithKey {
    text?: string | null;
}

export interface ISearchOptionsProps extends ElementWithKeyAndText {
    secondaryText?: string | null;
    imageUrl?: string | null;
    className?: string;
    showSecondaryText?: boolean | null;
    displayText?: string; // Add displayText for full description in tooltips
}

// Helper function to extract initials from text
const getInitialsFromText = (text: string): string => {
    // Split by common separators and get first letter of each word
    const words = text.split(/[\s\-_.,]+/).filter(word => word.length > 0);
    if (words.length === 0) return text.substring(0, 2).toUpperCase();

    // Take first letter of first two words, or first two letters if only one word
    if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    }

    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

// Helper function to extract contact avatar name
const getContactAvatarName = (option: ISearchOptionsProps): string | undefined => {
    if (option.displayText?.includes(' - ')) {
        // Extract name part for contact avatar (e.g., "John Doe - Manager" -> "John Doe")
        return option.displayText.split(' - ')[0].trim();
    }
    // Use the text directly for contact names
    return option.text || undefined;
};

// Helper function to extract non-contact avatar name
const getNonContactAvatarName = (option: ISearchOptionsProps): string | undefined => {
    const caseNumberRegex = /^\d+\/\d+$/;

    // If it's just a case/item number, use initials from description
    if (option.text && caseNumberRegex.exec(option.text)) {
        if (option.displayText?.includes(' - ')) {
            // Extract description part and get initials (e.g., "24/00002 - Test Document" -> "TD")
            const description = option.displayText.split(' - ')[1];
            if (description) {
                return getInitialsFromText(description);
            }
        }
        // Fallback to initials from the text itself
        return getInitialsFromText(option.text);
    }

    // For other items, extract meaningful text and get initials
    if (option.displayText?.includes(' - ')) {
        // Extract description part for avatar (e.g., "Project - Test Document" -> "TD")
        const parts = option.displayText.split(' - ');
        if (parts.length > 1) {
            return getInitialsFromText(parts[1].trim());
        }
    }

    if (option.text?.includes(' - ')) {
        const description = option.text.split(' - ')[1];
        return description ? getInitialsFromText(description) : getInitialsFromText(option.text);
    }

    // Fallback to initials from the text
    return option.text ? getInitialsFromText(option.text) : undefined;
};

// QuickSearchAvatar component
const QuickSearchAvatar = (props: { option: ISearchOptionsProps; isContact?: boolean }) => {
    const { option, isContact = false } = props;

    const avatarName = isContact
        ? getContactAvatarName(option)
        : getNonContactAvatarName(option);

    return <Avatar name={avatarName} color="colorful" image={{ src: option.imageUrl ?? undefined }} />;
};

// Tooltip component for overflow text (simplified version)
const TooltipOverflowText = ({ text, maxLength }: { text?: string | null; maxLength?: number }) => {
    if (!text) return <span>{text}</span>;

    // Show the full text in tag (no extraction of case number only)
    const shouldTruncate = maxLength && text.length > maxLength;
    const displayText = shouldTruncate ? `${text.substring(0, maxLength)}...` : text;

    // Use full text for tooltip as well
    const tooltipText = text;

    return (
        <span
            title={tooltipText}
            style={{
                display: 'inline-block',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }}
        >
            {displayText}
        </span>
    );
};

export interface QuickSearchTagProps {
    option: ISearchOptionsProps;
    showSecondaryTextInSelection?: boolean;
    hideCoin?: boolean;
    disabled?: boolean;
    customAvatar?: (option: ISearchOptionsProps) => React.JSX.Element;
    size?: 'small' | 'medium'; // Add size prop
    maxTextLength?: number; // Add truncation prop
    isContact?: boolean; // Flag to indicate if this item represents a contact
    withDismissButton?: boolean; // Flag to indicate if tag has a dismiss button
}

const useStyles = makeStyles({
    selectedItem: {
        height: '24px',
        maxWidth: '250px' // Prevent tags from becoming too wide
    },
    selectedItemWithSecondaryText: {
        height: '36px',
        maxWidth: '250px'
    },
    tagContent: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%'
    },
    tagWithDismissButton: {
        paddingRight: '4px' // Reduce right padding when dismiss button is present
    }
});

export const QuickSearchTag = (props: QuickSearchTagProps) => {
    const { option, hideCoin, customAvatar, size = 'small', maxTextLength, isContact = false, withDismissButton = false } = props;
    const styles = useStyles();
    const selectedItemClass = mergeClasses(
        styles.selectedItem,
        props.showSecondaryTextInSelection && styles.selectedItemWithSecondaryText,
        styles.tagContent,
        withDismissButton && styles.tagWithDismissButton
    );

    const avatarElement = customAvatar ? customAvatar(option) : <QuickSearchAvatar option={option} isContact={isContact} />;

    return (
        <Tag
            key={option.key}
            shape="rounded"
            size={size}
            media={hideCoin ? null : avatarElement}
            secondaryText={props.showSecondaryTextInSelection ? option.secondaryText : undefined}
            className={selectedItemClass}
            disabled={props.disabled}
            dismissible={false}
        >
            <TooltipOverflowText text={option.text} maxLength={maxTextLength} />
        </Tag>
    );
};

export default QuickSearchTag;
